import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/contexts/unified-permissions';
import { useOfflineState } from '@/contexts/offline/useOfflineState';
import { indexedDBService, offlineDB } from '@/services/offline/indexeddb';
import { STORES } from '@/services/offline/indexeddb/schema';
import { WeeklyReport } from '@/types/weeklyReports';
import type { WeeklyReportOffline } from '@/services/offline/indexeddb/schema';
import type { Json } from '@/integrations/supabase/types';

/**
 * Convert offline weekly report to WeeklyReport type
 */
function convertOfflineToWeeklyReport(offlineReport: WeeklyReportOffline): WeeklyReport {
  return {
    id: offlineReport.id,
    user_id: offlineReport.user_id,
    week_start: offlineReport.week_start,
    ai_insights: offlineReport.ai_insights || null,
    summary: offlineReport.summary || null,
    email_status: {
      status: offlineReport.email_status.status,
      attempts: offlineReport.email_status.attempts,
      last_attempt: offlineReport.email_status.last_attempt,
      error: offlineReport.email_status.error,
      sent_at: offlineReport.email_status.sent_at,
    },
    feedback_rating: offlineReport.feedback_rating || null,
    has_training: offlineReport.has_training || null,
    included_competition_data: offlineReport.included_competition_data || null,
    insight_type: offlineReport.insight_type || null,
    model_version: offlineReport.model_version || null,
    recommendations: offlineReport.recommendations as Json || null,
    technique_tags: offlineReport.technique_tags || null,
    training_count: offlineReport.training_count || null,
    training_minutes: offlineReport.training_minutes || null,
    competition_summary: offlineReport.competition_summary as Json || null,
    viewed_at: offlineReport.viewed_at || null,
    created_at: offlineReport.created_at,
    updated_at: offlineReport.updated_at || null
  };
}

/**
 * Offline-first hook for weekly insights
 * Similar to useSessionQueries but for weekly reports
 */
export const useOfflineWeeklyInsights = () => {
  const [weeklyInsights, setWeeklyInsights] = useState<WeeklyReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = usePermissions();
  const { state } = useOfflineState();

  const fetchWeeklyInsights = useCallback(async () => {
    if (!user?.id) {
      setWeeklyInsights([]);
      setIsLoading(false);
      return [];
    }

    const userId = user.id;

    try {
      // Initialize IndexedDB if needed
      await indexedDBService.initialize();

      // Always try cache first for offline-first approach
      const cachedReports = await indexedDBService.weeklyReports.getByUserId(userId);

      // If we have cached data and we're offline, use it
      if (cachedReports.length > 0 && !state.isOnline) {
        const sortedReports = cachedReports
          .map(convertOfflineToWeeklyReport)
          .sort((a, b) => new Date(b.week_start).getTime() - new Date(a.week_start).getTime()); // Newest first
        setWeeklyInsights(sortedReports);
        setIsLoading(false);
        return sortedReports;
      }

      // If we have cached data but we're online, still show cached data first
      if (cachedReports.length > 0 && state.isOnline) {
        const sortedReports = cachedReports
          .map(convertOfflineToWeeklyReport)
          .sort((a, b) => new Date(b.week_start).getTime() - new Date(a.week_start).getTime()); // Newest first
        setWeeklyInsights(sortedReports);
      }

      // If no cached data and we're offline, return empty array
      if (!state.isOnline) {
        setWeeklyInsights([]);
        setIsLoading(false);
        return [];
      }
      const { data, error: fetchError } = await supabase
        .from('weekly_reports')
        .select('*')
        .eq('user_id', userId)
        .or('ai_insights.not.is.null,summary.not.is.null')
        .order('week_start', { ascending: false });

      if (fetchError) {
        console.error('Database error fetching weekly insights:', fetchError);
        throw new Error(`Failed to fetch weekly insights: ${fetchError.message}`);
      }

      // Helper function to safely parse email status
      const parseEmailStatus = (emailStatus: Json): WeeklyReport['email_status'] => {
        if (!emailStatus || typeof emailStatus !== 'object') {
          return {
            status: 'pending',
            attempts: 0,
            last_attempt: null,
            error: null,
            sent_at: null,
          };
        }

        const statusObj = emailStatus as Record<string, unknown>;
        const status = statusObj.status;

        return {
          status: status === 'unknown'
            ? 'pending'
            : (['pending', 'sent', 'failed', 'retry'].includes(status as string)
                ? status as 'pending' | 'sent' | 'failed' | 'retry'
                : 'pending'),
          attempts: typeof statusObj.attempts === 'number' ? statusObj.attempts : 0,
          last_attempt: typeof statusObj.last_attempt === 'string' ? statusObj.last_attempt : null,
          error: typeof statusObj.error === 'string' ? statusObj.error : null,
          sent_at: typeof statusObj.sent_at === 'string' ? statusObj.sent_at : null,
        };
      };

      const mappedInsights: WeeklyReport[] = (data || []).map((report: WeeklyReport) => ({
        id: report.id,
        week_start: report.week_start,
        ai_insights: report.ai_insights,
        competition_summary: report.competition_summary,
        created_at: report.created_at,
        email_status: parseEmailStatus(report.email_status),
        feedback_rating: report.feedback_rating,
        has_training: report.has_training,
        included_competition_data: report.included_competition_data,
        insight_type: report.insight_type,
        model_version: report.model_version,
        recommendations: report.recommendations,
        summary: report.summary,
        technique_tags: Array.isArray(report.technique_tags) ? report.technique_tags : null,
        training_count: report.training_count,
        training_minutes: report.training_minutes,
        updated_at: report.updated_at,
        user_id: report.user_id,
        viewed_at: report.viewed_at,
      }));

      // Cache the fresh data in IndexedDB
      if (mappedInsights.length > 0) {
        try {
          // Clear existing cache for this user first
          const existingReports = await indexedDBService.weeklyReports.getByUserId(userId);
          for (const existingReport of existingReports) {
            await offlineDB.delete(STORES.WEEKLY_REPORTS, existingReport.id);
          }

          // Cache new data
          await Promise.all(
            mappedInsights.map(async (insight) => {
              const offlineInsight: WeeklyReportOffline = {
                id: insight.id,
                user_id: insight.user_id || userId,
                week_start: insight.week_start,
                ai_insights: insight.ai_insights || undefined,
                summary: insight.summary || undefined,
                email_status: {
                  status: insight.email_status.status,
                  attempts: insight.email_status.attempts,
                  last_attempt: insight.email_status.last_attempt,
                  error: insight.email_status.error,
                  sent_at: insight.email_status.sent_at,
                },
                feedback_rating: insight.feedback_rating || undefined,
                has_training: insight.has_training || undefined,
                included_competition_data: insight.included_competition_data || undefined,
                insight_type: insight.insight_type || undefined,
                model_version: insight.model_version || undefined,
                recommendations: insight.recommendations || undefined,
                technique_tags: insight.technique_tags || undefined,
                training_count: insight.training_count || undefined,
                training_minutes: insight.training_minutes || undefined,
                competition_summary: insight.competition_summary || undefined,
                viewed_at: insight.viewed_at || undefined,
                created_at: insight.created_at || new Date().toISOString(),
                updated_at: insight.updated_at || new Date().toISOString(),
                _sync_status: 'synced' as const
              };

              return offlineDB.put(STORES.WEEKLY_REPORTS, offlineInsight);
            })
          );
        } catch (cacheError) {
          console.error('❌ Failed to cache weekly insights:', cacheError);
        }
      }

      setWeeklyInsights(mappedInsights);
      setIsLoading(false);
      return mappedInsights;
    } catch (error) {
      console.error('Error in fetchWeeklyInsights:', error);

      // Try to get cached data as fallback
      try {
        const cachedReports = await indexedDBService.weeklyReports.getByUserId(userId);
        if (cachedReports.length > 0) {
          const sortedReports = cachedReports
            .map(convertOfflineToWeeklyReport)
            .sort((a, b) => new Date(b.week_start).getTime() - new Date(a.week_start).getTime()); // Newest first
          setWeeklyInsights(sortedReports);
          setIsLoading(false);
          return sortedReports;
        }
      } catch (cacheError) {
        console.warn('Failed to get cached insights as fallback:', cacheError);
      }

      setError(error instanceof Error ? error.message : 'Failed to fetch weekly insights');
      setWeeklyInsights([]);
      setIsLoading(false);
      return [];
    }
  }, [user, state.isOnline]);

  useEffect(() => {
    fetchWeeklyInsights();
  }, [fetchWeeklyInsights]);

  return {
    weeklyInsights,
    fetchWeeklyInsights,
    isLoading,
    error,
    // Legacy compatibility
    insights: weeklyInsights,
    loading: isLoading
  };
};
